import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { fetch_page_load_more } from "../../../../actions/Page/asyncDispatchers";
import FeaturedNews from "../../../../components/FeaturedArea/components/FeaturedNews/component";
import ColumnPolicy from "../../../../components/ContentArea/library/ColumnPolicy";
import VerticalCardList from "../../../../components/VerticalCardList/component";

import styles from "../../styles/themes/default.module.scss";
import { DISPLAY_TYPES } from "../../../../components/ContentList/constants";
import MobilePolicy from "../../../../components/ContentArea/library/MobilePolicy";
import LoadMore from "../../../../components/ContentList/components/LoadMore/component";
import {
    SectionHeadingContainer,
    HiddenSectionHeading,
} from "../SectionHeadingContainer/component";
import { isLinkVisible, addCardTrackingClasses } from "../../utils/library";
import {
    DEFAULT_KEY,
    VERTICAL_LIST_MAIN,
    FEATURED_CONDENSED_KEY,
} from "../../constants";
import { POSITION_MAIN } from "../../../../constants/page";
import { getLayout } from "../../../../components/ContentGrid/utils/mapping";
import ContentGrid from "../../../../components/ContentGrid/component";

/**
 * This component handles creating the proper component structure for a lineup section
 *
 * @param {Object} params the section params
 * @param {string} [position=POSITION_MAIN] position in main (rather than right rail)
 * @param {number} sectionIndex the index of the current section
 * @param {number} contentIndex the content item index
 * @param {string} viewport viewport
 *
 * Key properties of the @param params object
 * @param {Array} params.content the lineup content to pass to ContentGrid
 * @param {Object} params.rest the additional parameters
 *
 * @returns {JSX} component structure for lineup section
 */

const LineupContainer = ({
    params,
    position = POSITION_MAIN,
    sectionIndex,
    contentIndex,
    viewport,
}) => {
    const path = useSelector((state) => state.app?.path);
    let layout = useSelector(
        (state) => state.page?.sections[sectionIndex]?.main?.layout
    );
    const initialTotalCards = useSelector(
        (state) => state.page?.initialTotalCards
    );
    const loadMoreCallback = fetch_page_load_more(initialTotalCards);

    if (!params?.content) {
        return null;
    }

    if (layout === "default") {
        layout = DEFAULT_KEY;
    }

    if (position === POSITION_MAIN) {
        const heading = params.wrapper?.heading?.visible ? (
            <SectionHeadingContainer
                title={params.wrapper?.heading?.title}
                link={params.wrapper.link.url}
                linkVisible={isLinkVisible(params.wrapper.link.visible)}
                description={params.wrapper.heading.description}
            />
        ) : (
            <HiddenSectionHeading title={params.wrapper?.heading?.title} />
        );
        const containerLayout = getLayout(layout);

        let gridData = params.content;
        if (params.wrapper?.size) {
            gridData = gridData.slice(0, params.wrapper.size);
        }
        gridData = addCardTrackingClasses(gridData, sectionIndex);

        const columnPolicy =
            viewport === "viewportSmall"
                ? new MobilePolicy()
                : new ColumnPolicy();
        if (layout === VERTICAL_LIST_MAIN) {
            return (
                <VerticalCardList
                    title={params.wrapper?.heading?.title}
                    items={gridData}
                    hideTitle={!params.wrapper?.heading?.visible}
                    displayPolicy={columnPolicy}
                    displayType={DISPLAY_TYPES.TYPE_DEFAULT}
                    className="flp-verticallist"
                    url={params.wrapper?.link?.url}
                />
            );
        }

        if (layout === FEATURED_CONDENSED_KEY) {
            return (
                <FeaturedNews
                    items={gridData}
                    viewport={viewport}
                    title={params.wrapper?.heading?.title}
                />
            );
        }

        // If loadmore enabled in Polopoly, callback exist and
        // loadmore content is more than the initial total cards
        const loadMoreButton =
            sectionIndex !== 0 &&
            params.wrapper?.loadMoreContent &&
            loadMoreCallback &&
            !params.disableLoadMore ? (
                <div
                    className="loadMoreFLPWrapper"
                    data-cy="load-more-FLP-wrapper"
                >
                    <LoadMore
                        fetchFunction={loadMoreCallback({
                            sectionIndex,
                            contentIndex,
                            position,
                            slug: params?.lineup?.slug,
                            pubQueueId: params?.lineup?.pubQueueId,
                        })}
                    />
                </div>
            ) : null;
        const className = classNames(
            params?.wrapper?.size > 4 && styles.fourColPlus,
            loadMoreButton && styles.loadMore
        );
        return (
            <>
                {heading}
                <ContentGrid
                    className={className}
                    gridLayout={containerLayout}
                    gridData={gridData}
                    layoutName={layout}
                    path={path}
                    featureInstance={
                        params.wrapper?.attribution?.featureInstance
                    }
                />
                {loadMoreButton}
            </>
        );
    }

    return (
        <VerticalCardList
            title={params.wrapper?.heading?.title}
            items={[...params.content]}
            hideTitle={!params.wrapper?.heading?.visible}
            featureInstance={params.wrapper?.attribution?.featureInstance}
        />
    );
};

LineupContainer.propTypes = {
    params: PropTypes.object,
    contentIndex: PropTypes.number,
    sectionIndex: PropTypes.number,
    position: PropTypes.string,
    viewport: PropTypes.string,
};

export default LineupContainer;
